/**
 * Admin Session Backup Utilities
 *
 * Before swapping to the Doctor's session during impersonation, we snapshot
 * the Admin's Amplify auth tokens so they can be restored seamlessly after
 * impersonation ends.
 *
 * Stored in localStorage (not sessionStorage) so the backup is visible from
 * every tab. The impersonation *state* already syncs across tabs via
 * localStorage, so MA/Admin users — who routinely work across many tabs —
 * frequently end an assume from a different tab than the one that started it.
 * A tab-scoped sessionStorage backup is absent there, which used to strand them
 * on the login page. The admin's real tokens already live in localStorage while
 * they're signed in, so a duplicate copy is not a new class of exposure; we
 * bound how long an orphaned copy can linger with BACKUP_TTL_MS and always
 * clear it on restore.
 */

const ADMIN_SESSION_BACKUP_KEY = "impersonation_admin_session_backup";
// Max age a backup can be restored. Comfortably exceeds the 30-min impersonation
// session cap so any legitimate end restores, while bounding how long a copy of
// the admin's tokens can sit unused after an abandoned/orphaned impersonation.
const BACKUP_TTL_MS = 60 * 60 * 1000;

interface AdminSessionBackup {
  entries: Record<string, string>;
  backedUpAt: string;
}

/**
 * Read the backup from localStorage, returning null (and clearing it) if it's
 * missing, unparseable, or older than BACKUP_TTL_MS.
 */
function readValidBackup(): AdminSessionBackup | null {
  const raw = localStorage.getItem(ADMIN_SESSION_BACKUP_KEY);
  if (!raw) return null;

  let backup: AdminSessionBackup;
  try {
    backup = JSON.parse(raw) as AdminSessionBackup;
  } catch {
    localStorage.removeItem(ADMIN_SESSION_BACKUP_KEY);
    return null;
  }

  const ageMs = Date.now() - new Date(backup.backedUpAt).getTime();
  if (!Number.isFinite(ageMs) || ageMs > BACKUP_TTL_MS) {
    localStorage.removeItem(ADMIN_SESSION_BACKUP_KEY);
    return null;
  }
  return backup;
}

/** Decode a JWT payload segment. Null on any malformation. */
function decodeJwtPayload(jwt: string): Record<string, unknown> | null {
  try {
    const seg = jwt.split(".")[1];
    if (!seg) return null;
    const padded = seg
      .replace(/-/g, "+")
      .replace(/_/g, "/")
      .padEnd(Math.ceil(seg.length / 4) * 4, "=");
    return JSON.parse(atob(padded)) as Record<string, unknown>;
  } catch {
    return null;
  }
}

/**
 * Decoded payload of the currently-active Amplify idToken in localStorage, or
 * null if there's no session / it can't be parsed. Lets callers tell whose
 * session is live right now — e.g. whether it's an impersonated one
 * (custom:impersonator_sub present) or a plain admin session.
 */
export function getCurrentIdTokenPayload(): Record<string, unknown> | null {
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (
      key?.startsWith("CognitoIdentityServiceProvider.") &&
      key.endsWith(".idToken")
    ) {
      const jwt = localStorage.getItem(key);
      return jwt ? decodeJwtPayload(jwt) : null;
    }
  }
  return null;
}

/**
 * Snapshot all Amplify auth tokens from localStorage.
 * Amplify v6 stores tokens under keys matching:
 *   CognitoIdentityServiceProvider.<clientId>.*
 *
 * Returns false without writing if the current session is itself an
 * impersonated one — snapshotting that would capture the target's tokens as the
 * "admin" backup (possible if another tab swapped the shared localStorage
 * session mid-flight), corrupting recovery. The caller resolves the failure.
 */
export function backupAdminSession(): boolean {
  if (getCurrentIdTokenPayload()?.["custom:impersonator_sub"]) return false;

  const backup: Record<string, string> = {};
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("CognitoIdentityServiceProvider.")) {
      backup[key] = localStorage.getItem(key)!;
    }
  }
  localStorage.setItem(
    ADMIN_SESSION_BACKUP_KEY,
    JSON.stringify({
      entries: backup,
      backedUpAt: new Date().toISOString(),
    } satisfies AdminSessionBackup),
  );
  return true;
}

/**
 * Restore the Admin's Amplify auth tokens from sessionStorage,
 * replacing the current (Doctor's) tokens.
 * Returns true if restoration succeeded.
 */
export function restoreAdminSession(): boolean {
  const backup = readValidBackup();
  if (!backup) return false;

  // Clear current (Doctor's) auth tokens
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("CognitoIdentityServiceProvider.")) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));

  // Restore Admin's tokens
  Object.entries(backup.entries).forEach(([key, value]) => {
    localStorage.setItem(key, value);
  });

  // Clean up backup
  localStorage.removeItem(ADMIN_SESSION_BACKUP_KEY);
  return true;
}

export function hasAdminSessionBackup(): boolean {
  return readValidBackup() !== null;
}

/**
 * Decode the admin's email from the backed-up idToken (only valid while
 * impersonation is active). Used so dev-only features like the sandbox
 * toggle can derive a name from the original logged-in user, not the
 * impersonated user. Returns null if no backup exists or the token can't
 * be parsed.
 */
export function getAdminEmailFromBackup(): string | null {
  const backup = readValidBackup();
  if (!backup) return null;
  const idTokenKey = Object.keys(backup.entries).find((k) =>
    k.endsWith(".idToken"),
  );
  if (!idTokenKey) return null;
  const email = decodeJwtPayload(backup.entries[idTokenKey])?.email;
  return typeof email === "string" ? email : null;
}

/**
 * Clear all Cognito auth tokens from localStorage without calling Amplify's signOut().
 * This avoids triggering Hub events and Authenticator state changes.
 */
export function clearCognitoLocalStorageKeys(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (key?.startsWith("CognitoIdentityServiceProvider.")) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => localStorage.removeItem(key));
}
